import { useState } from "react";
import {
  TbCalendar,
  TbPackage,
  TbMapPin,
  TbClock,
  TbCheck,
  TbX,
  TbRuler,
  TbPhoto,
} from "react-icons/tb";
import { ImageModal } from "../Orders/OrderDetailCard";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import toast from "react-hot-toast";

const QuotationDetailsContent = ({ q }) => {
  const latestOFM =
    q.ofm_Impressions?.length > 0
      ? [...q.ofm_Impressions].sort(
        (a, b) => new Date(b.date) - new Date(a.date)
      )[0]
      : null;

  const holdingDays =
    q.holdingPeriod?.from && q.holdingPeriod?.to
      ? Math.ceil(
        (new Date(q.holdingPeriod.to) -
          new Date(q.holdingPeriod.from)) /
        (1000 * 60 * 60 * 24)
      )
      : null;

  const [modalOpen, setModalOpen] = useState(false);
  const [modalIndex, setModalIndex] = useState(0);

  const allMedia = [
    ...(q.media?.images || []),
    ...(q.media?.videos || []),
  ];

  const isVideo = (url) =>
    url?.includes(".mp4") ||
    url?.includes(".webm") ||
    url?.includes("video");

  const downloadMedia = async (url, idx) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const extension = blob.type.split("/")[1]?.split("+")[0] || "jpg";
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = `reference-media-${idx + 1}.${extension}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(objectUrl);
    } catch (err) {
      console.error("Download failed:", err);
      toast.error("Download failed. Opening in new tab instead.");
      window.open(url, "_blank");
    }
  };

  const downloadAllMedia = async () => {
    const toastId = "dl-all-ref";
    toast.loading(`Preparing zip for ${allMedia.length} files...`, { id: toastId });
    try {
      const zip = new JSZip();
      const folder = zip.folder("reference-media");
      await Promise.all(
        allMedia.map(async (url, idx) => {
          try {
            const response = await fetch(url);
            const blob = await response.blob();
            const extension = blob.type.split("/")[1]?.split("+")[0] || "jpg";
            folder.file(`reference-media-${idx + 1}.${extension}`, blob);
          } catch (err) {
            console.error(`Failed to fetch media ${idx + 1}:`, err);
          }
        })
      );
      toast.loading("Zipping files...", { id: toastId });
      const zipBlob = await zip.generateAsync({ type: "blob" });
      saveAs(zipBlob, `reference-media-${q._id?.slice(-6) || "files"}.zip`);
      toast.dismiss(toastId);
      toast.success(`Downloaded ${allMedia.length} files as zip ✅`);
    } catch (err) {
      toast.dismiss(toastId);
      toast.error("Zip creation failed. Try downloading individually.");
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-5 mt-4 sm:mt-5">

      {/* LEFT COLUMN - Main Details */}
      <div className="lg:col-span-2 space-y-4">

        {/* PRICING & QUANTITY - Enhanced */}
        <div className="bg-white p-4 sm:p-5 rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center gap-2 mb-4">
            <div className="p-2 bg-primaryColor/10 rounded-lg">
              <TbPackage className="text-primaryColor text-xl" />
            </div>
            <h3 className="font-semibold text-gray-800 text-base sm:text-lg">Pricing & Quantity</h3>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
            <div className="bg-gray-50 p-3 rounded-lg border border-gray-100">
              <p className="text-gray-500 text-xs mb-1.5 font-medium">Price</p>
              <p className="font-bold text-primaryColor text-sm sm:text-base">
                {q.currency} {q.pricing?.commissionAdjustedAmount || "N/A"}
              </p>
              <p className="text-[10px] text-gray-400 mt-0.5">{`Price per ${q.standardizedUnit}` || "Per unit"}</p>
            </div>

            <div className="bg-gray-50 p-3 rounded-lg border border-gray-100">
              <p className="text-gray-500 text-xs mb-1.5 font-medium">Unit</p>
              <p className="font-semibold text-gray-800 text-sm">{q.standardizedUnit}</p>
            </div>

            <div className="bg-gray-50 p-3 rounded-lg border border-gray-100">
              <p className="text-gray-500 text-xs mb-1.5 font-medium">Volume</p>
              <p className="font-semibold text-gray-800 text-sm">{q.quantity?.volume}</p>
            </div>

            <div className="bg-gray-50 p-3 rounded-lg border border-gray-100">
              <p className="text-gray-500 text-xs mb-1.5 font-medium">Availability</p>
              <p className={`font-semibold text-sm ${q.quantity?.isAvailableFull ? "text-green-600" : "text-orange-600"}`}>
                {q.quantity?.isAvailableFull ? "Full" : "Partial"}
              </p>
            </div>
          </div>
        </div>

        {/* PACK SIZE - Enhanced */}
        <div className="bg-white p-4 sm:p-5 rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center gap-2 mb-4">
            <div className="p-2 bg-blue-50 rounded-lg">
              <TbRuler className="text-blue-600 text-xl" />
            </div>
            <h3 className="font-semibold text-gray-800 text-base sm:text-lg">Pack Size</h3>
          </div>

          <div className="flex flex-wrap gap-3">
            <div className="flex items-center gap-2 bg-gray-50 px-3 py-2 rounded-lg border border-gray-100">
              <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" />
              </svg>
              <span className="text-sm font-semibold text-gray-700">
                {q.packSize?.weight} {q.packSize?.unit || q.standardizedUnit || "Kg"}
              </span>
            </div>

            <div className="flex items-center gap-2 bg-gray-50 px-3 py-2 rounded-lg border border-gray-100">
              <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
              </svg>
              <span className="text-sm text-gray-700">
                <span className="font-semibold">{q.packSize?.dimensions?.length}</span> ×
                <span className="font-semibold"> {q.packSize?.dimensions?.breadth}</span> ×
                <span className="font-semibold"> {q.packSize?.dimensions?.height}</span>
                <span className="text-xs text-gray-500 ml-1">{q.packSize?.dimensions?.measured}</span>
              </span>
            </div>
          </div>
        </div>

        {/* HOLDING PERIOD - Enhanced */}
        <div className="bg-white p-4 sm:p-5 rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center gap-2 mb-4">
            <div className="p-2 bg-green-50 rounded-lg">
              <TbClock className="text-green-600 text-xl" />
            </div>
            <h3 className="font-semibold text-gray-800 text-base sm:text-lg">Holding Period</h3>
          </div>

          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-3">
            <div className="flex items-center gap-2 bg-gray-50 px-3 py-2 rounded-lg border border-gray-100">
              <TbCalendar className="text-gray-600" />
              <span className="text-sm font-medium text-gray-700">
                {new Date(q.holdingPeriod?.from).toLocaleDateString("en-GB", {
                  day: "numeric",
                  month: "short",
                  year: "numeric"
                })}
              </span>
            </div>

            <div className="hidden sm:flex items-center">
              <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </div>

            <div className="flex items-center gap-2 bg-gray-50 px-3 py-2 rounded-lg border border-gray-100">
              <TbCalendar className="text-gray-600" />
              <span className="text-sm font-medium text-gray-700">
                {new Date(q.holdingPeriod?.to).toLocaleDateString("en-GB", {
                  day: "numeric",
                  month: "short",
                  year: "numeric"
                })}
              </span>
            </div>

            {holdingDays !== null && (
              <div className="flex items-center gap-2 bg-green-50 px-3 py-2 rounded-lg border border-green-100">
                <TbClock className="text-green-600" />
                <span className="text-sm font-semibold text-green-700">
                  {holdingDays} day{holdingDays > 1 ? "s" : ""}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* PICKUP ADDRESS - Enhanced */}
        {/* <div className="bg-white p-4 sm:p-5 rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center gap-2 mb-4">
            <div className="p-2 bg-purple-50 rounded-lg">
              <TbMapPin className="text-purple-600 text-xl" />
            </div>
            <h3 className="font-semibold text-gray-800 text-base sm:text-lg">Pickup Address</h3>
          </div>

          <div className="bg-gray-50 p-3 sm:p-4 rounded-lg border border-gray-100">
            <div className="flex items-start gap-3">
              <svg className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <p className="text-sm text-gray-700 leading-relaxed">
                {q.pickUpAddress?.addressLine1}, {q.pickUpAddress?.city},{" "}
                {q.pickUpAddress?.state}, {q.pickUpAddress?.country}
                {q.pickUpAddress?.pincode && (
                  <span className="block text-xs text-gray-500 mt-1">
                    PIN: {q.pickUpAddress.pincode}
                  </span>
                )}
              </p>
            </div>
          </div>
        </div> */}

        {/* REFERENCE MEDIA */}
        {allMedia.length > 0 && (
          <div className="bg-white p-4 sm:p-5 rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className="p-2 bg-pink-50 rounded-lg">
                  <TbPhoto className="text-pink-600 text-xl" />
                </div>
                <h3 className="font-semibold text-gray-800 text-base sm:text-lg">
                  Reference Media
                </h3>
              </div>

              {/* Download All as zip */}
              <button
                onClick={downloadAllMedia}
                className="flex items-center gap-1.5 text-xs font-bold text-primaryColor hover:text-primaryColor/80 transition-colors"
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5 5-5M12 4v11" />
                </svg>
                Download All
              </button>
            </div>

            <div className="flex flex-wrap gap-3">
              {allMedia.map((url, i) => (
                <div key={i} className="group relative w-24 h-24 sm:w-28 sm:h-28">
                  {/* View button */}
                  <button
                    onClick={() => {
                      setModalIndex(i);
                      setModalOpen(true);
                    }}
                    className="w-full h-full rounded-xl overflow-hidden border border-gray-200 hover:border-primaryColor/40 transition-all hover:shadow-md hover:scale-[1.02] bg-gray-100 block"
                  >
                    {isVideo(url) ? (
                      <div className="w-full h-full flex items-center justify-center bg-gray-900">
                        <span className="text-3xl">🎥</span>
                      </div>
                    ) : (
                      <img
                        src={url}
                        alt={`Media ${i + 1}`}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    )}
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors rounded-xl" />
                    <div className="absolute bottom-1.5 right-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                      <span className="bg-black/70 text-white text-[10px] px-1.5 py-0.5 rounded-md font-medium">
                        {isVideo(url) ? "Play" : "View"}
                      </span>
                    </div>
                  </button>

                  {/* Per-item download button */}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      downloadMedia(url, i);
                    }}
                    title="Download"
                    className="absolute top-1.5 right-1.5 z-10 w-6 h-6 rounded-md bg-black/60 hover:bg-primaryColor flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-200"
                  >
                    <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5 5-5M12 4v11" />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Q&A SECTION - Enhanced */}
        {q.specification?.relatedQuestionsAndAnswers?.length > 0 && (
          <div className="bg-white p-4 sm:p-5 rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center gap-2 mb-4">
              <div className="p-2 bg-orange-50 rounded-lg">
                <svg className="w-5 h-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="font-semibold text-gray-800 text-base sm:text-lg">Questions & Answers</h3>
            </div>

            <div className="space-y-3">
              {q.specification.relatedQuestionsAndAnswers.map((qa, i) => (
                <div key={i} className="bg-gray-50 p-3 sm:p-4 rounded-lg border border-gray-100">
                  <div className="flex items-start gap-3">
                    <div className={`p-1.5 rounded-full flex-shrink-0 ${qa.supplierAction?.isAgreed === "Yes"
                      ? "bg-green-100"
                      : "bg-red-100"
                      }`}>
                      {qa.supplierAction?.isAgreed === "Yes" ? (
                        <TbCheck className="text-green-600 text-sm" />
                      ) : (
                        <TbX className="text-red-600 text-sm" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm text-gray-800 mb-1">{qa.question}</p>
                      <p className="text-xs text-gray-600 mb-2">
                        <span className="font-medium">Answer:</span> {qa.answeredByBuyer}
                      </p>
                      <span className={`text-xs font-semibold px-2 py-1 rounded-full inline-block ${qa.supplierAction?.isAgreed === "Yes"
                        ? "bg-green-100 text-green-700"
                        : "bg-red-100 text-red-700"
                        }`}>
                        {qa.supplierAction?.isAgreed}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* OTHER REQUIREMENTS - Enhanced */}
        {q.specification?.otherRequirements?.length > 0 && (
          <div className="bg-white p-4 sm:p-5 rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center gap-2 mb-4">
              <div className="p-2 bg-indigo-50 rounded-lg">
                <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                </svg>
              </div>
              <h3 className="font-semibold text-gray-800 text-base sm:text-lg">Other Requirements</h3>
            </div>

            <div className="space-y-3">
              {q.specification.otherRequirements.map((item, i) => (
                <div key={i} className="bg-gray-50 p-3 sm:p-4 rounded-lg border border-gray-100">
                  <div className="flex items-start gap-3">
                    <div className={`p-1.5 rounded-full flex-shrink-0 ${item.supplierAction?.isAgreed === "Yes"
                      ? "bg-green-100"
                      : "bg-red-100"
                      }`}>
                      {item.supplierAction?.isAgreed === "Yes" ? (
                        <TbCheck className="text-green-600 text-sm" />
                      ) : (
                        <TbX className="text-red-600 text-sm" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-700 mb-2">{item.buyersRequirement}</p>
                      <span className={`text-xs font-semibold px-2 py-1 rounded-full inline-block ${item.supplierAction?.isAgreed === "Yes"
                        ? "bg-green-100 text-green-700"
                        : "bg-red-100 text-red-700"
                        }`}>
                        {item.supplierAction?.isAgreed}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* RIGHT COLUMN - Sidebar Info */}
      <div className="space-y-4">

        {/* REVIEW STATUS - Enhanced */}
        <div className="bg-white p-4 sm:p-5 rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center gap-2 mb-3">
            <div className={`p-2 rounded-lg ${q.review?.status === "Approved" ? "bg-green-50" :
              q.review?.status === "Rejected" ? "bg-red-50" : "bg-yellow-50"
              }`}>
              <svg className={`w-5 h-5 ${q.review?.status === "Approved" ? "text-green-600" :
                q.review?.status === "Rejected" ? "text-red-600" : "text-yellow-600"
                }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 className="font-semibold text-gray-800 text-base">Review Status</h3>
          </div>

          <div className="space-y-2">
            <span className={`inline-block text-sm px-3 py-1.5 rounded-full font-semibold ${q.review?.status === "Approved"
              ? "bg-green-100 text-green-700 border border-green-200"
              : q.review?.status === "Rejected"
                ? "bg-red-100 text-red-700 border border-red-200"
                : "bg-yellow-100 text-yellow-700 border border-yellow-200"
              }`}>
              {q.review?.status}
            </span>

            {q.review?.reviewedAt && (
              <p className="text-xs text-gray-500 mt-2">
                Reviewed on {new Date(q.review.reviewedAt).toLocaleDateString("en-GB", {
                  day: "numeric",
                  month: "short",
                  year: "numeric"
                })}
              </p>
            )}
          </div>
        </div>

        {/* OFM IMPRESSION - Enhanced */}
        {latestOFM && (
          <div className="bg-white p-4 sm:p-5 rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center gap-2 mb-3">
              <div className="p-2 bg-blue-50 rounded-lg">
                <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
              <h3 className="font-semibold text-gray-800 text-base">OFM Impression</h3>
            </div>

            <div className="bg-gray-50 p-3 rounded-lg border border-gray-100">
              <p className="font-medium text-sm text-gray-800 mb-1">
                {latestOFM.ofmId?.firstName} {latestOFM.ofmId?.lastName}
              </p>
              <p className="text-xs text-gray-500">{latestOFM.status}</p>
            </div>
          </div>
        )}

        {/* SAMPLE DETAILS - Enhanced */}
        {q.isSelectedByBuyer && (
          <div className="bg-white p-4 sm:p-5 rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center gap-2 mb-3">
              <div className="p-2 bg-amber-50 rounded-lg">
                <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
              </div>
              <h3 className="font-semibold text-gray-800 text-base">Sample</h3>
            </div>

            <div className="space-y-2 text-sm">
              <div className="flex items-center justify-between bg-gray-50 p-2 rounded-lg">
                <span className="text-gray-600">Requested</span>
                <span className={`font-semibold ${q.sampleDetails?.requested ? "text-green-600" : "text-gray-500"}`}>
                  {q.sampleDetails?.requested ? "Yes" : "No"}
                </span>
              </div>

              <div className="flex items-center justify-between bg-gray-50 p-2 rounded-lg">
                <span className="text-gray-600">Approval</span>
                <span className={`font-semibold ${q.sampleDetails?.approval === "Approved" ? "text-green-600" :
                  q.sampleDetails?.approval === "Rejected" ? "text-red-600" : "text-yellow-600"
                  }`}>
                  {q.sampleDetails?.approval || "Pending"}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* IMAGE MODAL */}
      {modalOpen && (
        <ImageModal
          images={q.media?.images || []}
          videos={q.media?.videos || []}
          initialIndex={modalIndex}
          onClose={() => setModalOpen(false)}
        />
      )}
    </div>
  );
};

export default QuotationDetailsContent;